import React from 'react';
import { AIPerformanceReport as AIPerformanceReportType } from '../types';
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

interface AIPerformanceReportProps {
  report: AIPerformanceReportType;
  onGenerateNewReport?: () => void;
}

const AIPerformanceReport: React.FC<AIPerformanceReportProps> = ({
  report,
  onGenerateNewReport
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };



  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">AI Performance Report</h3>
          <p className="text-sm text-gray-500">
            {report.period.charAt(0).toUpperCase() + report.period.slice(1)} Report • 
            {formatDate(report.startDate)} - {formatDate(report.endDate)}
          </p>
        </div>
        {onGenerateNewReport && (
          <button
            onClick={onGenerateNewReport}
            className="bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
          >
            Generate New Report
          </button>
        )}
      </div>

      {/* Overall Score */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-lg font-medium text-gray-900">Overall Performance Score</h4>
          <span className={`text-2xl font-bold ${getScoreColor(report.overallScore)}`}>
            {report.overallScore}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ${
              report.overallScore >= 90 ? 'bg-green-500' :
              report.overallScore >= 80 ? 'bg-blue-500' :
              report.overallScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${report.overallScore}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Strengths */}
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <ArrowTrendingUpIcon className="w-5 h-5 text-green-600 mr-2" />
            <h5 className="font-medium text-green-900">Strengths</h5>
          </div>
          <ul className="space-y-2">
            {report.strengths.map((strength, index) => (
              <li key={index} className="flex items-start">
                <CheckCircleIcon className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-green-800">{strength}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Areas for Improvement */}
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center mb-3">
            <ArrowTrendingDownIcon className="w-5 h-5 text-yellow-600 mr-2" />
            <h5 className="font-medium text-yellow-900">Areas for Improvement</h5>
          </div>
          <ul className="space-y-2">
            {report.weaknesses.map((weakness, index) => (
              <li key={index} className="flex items-start">
                <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-yellow-800">{weakness}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Learning Gaps */}
      <div className="mt-6 bg-red-50 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mr-2" />
          <h5 className="font-medium text-red-900">Identified Learning Gaps</h5>
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {report.learningGaps.map((gap, index) => (
            <div key={index} className="flex items-start">
              <div className="w-2 h-2 bg-red-400 rounded-full mr-2 mt-2 flex-shrink-0"></div>
              <span className="text-sm text-red-800">{gap}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Suggested Resources */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <LightBulbIcon className="w-5 h-5 text-blue-600 mr-2" />
          <h5 className="font-medium text-blue-900">Recommended Resources</h5>
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {report.suggestedResources.map((resource, index) => (
            <div key={index} className="flex items-start">
              <div className="w-2 h-2 bg-blue-400 rounded-full mr-2 mt-2 flex-shrink-0"></div>
              <span className="text-sm text-blue-800">{resource}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Next Steps */}
      <div className="mt-6 bg-purple-50 rounded-lg p-4">
        <div className="flex items-center mb-3">
          <ChartBarIcon className="w-5 h-5 text-purple-600 mr-2" />
          <h5 className="font-medium text-purple-900">Recommended Next Steps</h5>
        </div>
        <div className="space-y-2">
          {report.nextSteps.map((step, index) => (
            <div key={index} className="flex items-start">
              <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-200 text-purple-800 text-xs font-medium rounded-full mr-3">
                {index + 1}
              </span>
              <span className="text-sm text-purple-800">{step}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Report generated on {formatDate(report.createdAt)} using AI analysis
        </p>
      </div>
    </div>
  );
};

export default AIPerformanceReport;
